import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { Add } from "@dreamhub/designsystem/icons";
import ConfirmationModal from "@/components/layouts/ConfirmationModal";
import { Button } from "@/components/ui/button";
import IconContainer from "@/components/ui/icon-container";
import SearchInput from "@/components/ui/search-input";
import Text from "@/components/ui/text";
import {
  useDeleteProductBundle,
  useProductBundles,
  useServiceItemTypes
} from "../hooks";
import { ProductBundle, ProductV2 } from "../types";
import { BundleCard } from "./BundleCard";
import { BundleDrawer } from "./BundleDrawer";
import { BundlesEmptyState } from "./BundlesEmptyState";

const TRANSLATION_KEY = "settings.product.bundlesTab";

interface BundlesTabProps {
  product: ProductV2;
}

export const BundlesTab: React.FC<BundlesTabProps> = ({ product }) => {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");
  const { data, isError } = useProductBundles(product.id);
  const { data: serviceItemTypes = [] } = useServiceItemTypes();
  const deleteBundleMutation = useDeleteProductBundle();

  const [bundleToDelete, setBundleToDelete] = useState<ProductBundle | null>(
    null
  );
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeleteConfirmed, setIsDeleteConfirmed] = useState(false);
  // Null while closed; an empty object adds a bundle, one with a bundle edits it.
  const [drawer, setDrawer] = useState<{ bundle?: ProductBundle } | null>(null);
  // Held here so a card stays open while a search hides it.
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  // Deleted services are not offered in the drawer, so they don't count.
  const hasServices = product.serviceItems.some(
    (service) => !service.isDeleted
  );

  const setBundleExpanded = (bundleId: string, expanded: boolean) => {
    setExpandedIds((ids) => {
      const next = new Set(ids);
      if (expanded) next.add(bundleId);
      else next.delete(bundleId);
      return next;
    });
  };

  const requestDelete = (bundle: ProductBundle) => {
    setBundleToDelete(bundle);
    setIsDeleteConfirmed(false);
    setIsDeleteModalOpen(true);
  };

  const resetDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setBundleToDelete(null);
    setIsDeleteConfirmed(false);
  };

  const handleConfirmDelete = async () => {
    if (!bundleToDelete || deleteBundleMutation.isPending) return;
    const deletedId = bundleToDelete.id;
    try {
      await deleteBundleMutation.mutateAsync({
        pathParams: { productId: product.id, bundleId: deletedId }
      });
      // Deleted from its own drawer: the drawer has nothing left to edit.
      setDrawer((current) =>
        current?.bundle?.id === deletedId ? null : current
      );
    } catch {
      toast.error(t(`${TRANSLATION_KEY}.deleteModal.deleteError`));
    } finally {
      resetDeleteModal();
    }
  };

  const renderContent = () => {
    // Without data the list is unknown: never claim it is empty, and keep a
    // cached list on screen when only a refetch failed.
    if (!data) {
      if (!isError) return null;
      return (
        <Text
          variant="bodySecondary"
          className="w-full text-center"
          data-test="product-bundles-load-error"
        >
          {t(`${TRANSLATION_KEY}.loadError`)}
        </Text>
      );
    }

    const { bundles } = data;
    if (bundles.length === 0) {
      return (
        <BundlesEmptyState
          onAddBundle={() => setDrawer({})}
          hasServices={hasServices}
        />
      );
    }

    const normalizedSearch = search.trim().toLowerCase();
    const filtered = bundles.filter(({ name }) =>
      name.toLowerCase().includes(normalizedSearch)
    );

    return (
      <>
        <div className="flex w-full items-center gap-2">
          <SearchInput
            className="flex-1"
            placeholder={t(`${TRANSLATION_KEY}.searchPlaceholder`)}
            value={search}
            onChange={setSearch}
          />
          <Button
            onClick={() => setDrawer({})}
            disabled={!hasServices}
            tooltipText={
              hasServices
                ? undefined
                : t(`${TRANSLATION_KEY}.noServicesTooltip`)
            }
            data-test="product-bundles-add-button"
          >
            <IconContainer
              as="span"
              size="18"
              color="current"
              aria-hidden="true"
            >
              <Add />
            </IconContainer>
            {t(`${TRANSLATION_KEY}.addBundle`)}
          </Button>
        </div>
        {filtered.length === 0 ? (
          <Text
            variant="bodySecondary"
            className="w-full text-center"
            data-test="product-bundles-no-results"
          >
            {t(`${TRANSLATION_KEY}.noSearchResults`)}
          </Text>
        ) : (
          <div className="flex w-full flex-col gap-4">
            {filtered.map((bundle) => (
              <BundleCard
                key={bundle.id}
                bundle={bundle}
                serviceItemTypes={serviceItemTypes}
                isExpanded={expandedIds.has(bundle.id)}
                onExpandedChange={(expanded) =>
                  setBundleExpanded(bundle.id, expanded)
                }
                onEdit={(bundle) => setDrawer({ bundle })}
                onDelete={requestDelete}
              />
            ))}
          </div>
        )}
      </>
    );
  };

  return (
    <div
      className="flex w-full flex-col gap-4"
      data-test="product-page-bundles"
    >
      {renderContent()}
      {drawer && (
        <BundleDrawer
          key={drawer.bundle?.id ?? "new"}
          product={product}
          bundle={drawer.bundle}
          serviceItemTypes={serviceItemTypes}
          onClose={() => setDrawer(null)}
          onDelete={requestDelete}
        />
      )}
      <ConfirmationModal
        isOpen={isDeleteModalOpen}
        setIsOpen={setIsDeleteModalOpen}
        title={t(`${TRANSLATION_KEY}.deleteModal.title`)}
        confirmationText={t(`${TRANSLATION_KEY}.deleteModal.description`)}
        submitButtonText={t(`${TRANSLATION_KEY}.deleteModal.submit`)}
        handleSubmit={handleConfirmDelete}
        handleCancel={resetDeleteModal}
        checkboxLabel={t(`${TRANSLATION_KEY}.deleteModal.checkboxLabel`)}
        isCheckboxRequired
        onCheckboxChange={setIsDeleteConfirmed}
        isCheckboxChecked={isDeleteConfirmed}
        data-test="product-bundles-delete-modal"
      />
    </div>
  );
};
